/**
 * tm/pages/FPPortalTds.tsx — FP 포털 /fp/<token> 새 모습 (2026-10-12 · 웹 시안 3판 · 7곳 검수 반영)
 *  조회 전용 · 심플 — 기존 기능 그대로: 상담 일정 · 담당 고객 찾기 · 업무 단계 · 상담 기록(파일 받기) · 공유 자료 · 지도사 공유 메모
 *  바뀐 것: TDS 짜임 · 대표 배지 = 업체의 가장 급한 일(지남 > 회신 기다림 > 0~3 > 4~7 > 8일+) · 회신 대기 단계에 「답하기」(카톡 · 문자로 넘어감)
 *  보험사 색 = 머리 점 + 고른 줄 왼쪽 띠 두 곳만 · 숫자 줄 없음 · PC(1024+) 왼쪽 목록 / 오른쪽 자세히
 *  옛 화면 FPPortalStandalone.tsx 는 되돌리기용으로 남겨 둔다
 *  제목은 늘 「OOO 팀장님 × 임지도사」(지도사 2026-10-12) · 긴 이름은 「…」로 줄임
 */
import { PREVIEW, PreviewBar } from '../portal-ui/preview';   // 사무소가 미리 볼 때(2026-10-28)
import { saveLoanKey } from '../../tax/loanKey';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  AppBar, AsOf, Asset, Badge, Button, Divider, G, Group, Icon, JIDO, MoreText, clamp, isTouch, ListHeader, ListRow, FoldFooter, Para, BrandStrip, setBrand, StepRow, TEAL, TaskSummary, TermsBox, RoundResultBox, TextLink, Top,
  Sheet, taskIcon, usePortalStyles, useToast, useWide,
} from '../portal-ui/tds';
import { AskButtons, useContact } from '../portal-ui/contact';
import type { Contact } from '../portal-ui/contact';
import {
  asOfLabel, compareUrgency, d10, daysBetween, initialOf, isEnded, kstToday, leftBadge, md, mdLong, nowLine, progressOf,
  representative, short, stageText, stepDate, stepState, stepNote, nowSentence, taskBadge, waitsOn, roundClosedText,
  taskNews,
} from '../portal-ui/portalLogic';
import type { PTask } from '../portal-ui/portalLogic';
import GovForCompany from '../portal-ui/GovForCompany';
import ForwardToCeo from '../portal-ui/ForwardToCeo';
import PortalRecordings from '../components/PortalRecordings';
import { ceoShown } from '../types';

interface FileRef { name: string; s3_key?: string }
interface PFP { id: string; name: string; company: string; title?: string }
interface PCompany { id: string; name: string; ceo?: string; biz_no?: string; real_ceo?: string; call_rec_count?: number }
interface PConsult { id: string; date: string; time?: string; company_id?: string; company_ids?: string[]; company_name?: string; status: string; location?: string; notes?: string; content?: string; files?: FileRef[]; rec_count?: number }
interface PComment { id: string; company_id: string; author: string; content: string; created_at: string }
interface PData { fp: PFP; loan?: { token: string; key: string } | null; insuranceColor?: string | null; contact?: Contact; companies: PCompany[]; tasks: PTask[]; consultations: PConsult[]; comments?: PComment[] }
interface StorageFile { id: string; company_id: string; file_name: string; item_title?: string; source?: string; uploaded_at?: string }

export default function FPPortalTds({ fpId }: { fpId: string }) {
  usePortalStyles();
  const wide = useWide();
  const [data, setData] = useState<PData | null>(null);
  const [files, setFiles] = useState<StorageFile[]>([]);
  const [error, setError] = useState('');
  const [selId, setSelId] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const [reply, setReply] = useState<string | null>(null);
  const [opening, setOpening] = useState<string | null>(null);
  const [recFor, setRecFor] = useState<PConsult | null>(null);   // 녹음 · 대화 글 보는 상담(2026-10-28)   // 「받기」 누른 파일(여는 중)
  const [callFor, setCallFor] = useState<PCompany | null>(null);   // 통화 녹음 보는 업체(2026-10-28 · 문자 주소 ?rec= 로 바로)
  const listY = useRef(0);   // 목록으로 돌아올 때 같은 자리로
  const [toastNode, toast] = useToast();

  useEffect(() => {
    fetch(`/api/tm/fp-portal-data/${encodeURIComponent(fpId)}`).then(r => r.json())
      .then((j: { success?: boolean; data?: PData; error?: string }) => { if (j.success && j.data) setData(j.data); else setError('이 주소는 이제 쓰지 않아요. 임지도사에게 새 주소를 받아 주세요'); })
      .catch(() => setError('잠시 연결이 안 돼요. 조금 뒤 다시 열어 주세요'));
    fetch(`/api/tm/fp-storage/${encodeURIComponent(fpId)}`).then(r => r.json())
      .then((j: { success?: boolean; data?: StorageFile[] }) => { if (j.success && Array.isArray(j.data)) setFiles(j.data); })
      .catch(() => { /* 공유 자료가 없어도 화면은 그대로 */ });
  }, [fpId]);

  useEffect(() => { if (data) document.title = `${data.fp.name} 팀장님 × ${JIDO}`; }, [data]);
  // 포털을 연 기기 = 팀장님 기기 — 대출 링크 「보낸 대출 진행 보기」가 인증번호 없이 열리게 열쇠를 둔다(2026-10-28 지도사 「허들 없애」)
  useEffect(() => { if (data && data.loan) saveLoanKey(data.loan.token, data.loan.key); }, [data]);
  // 문자 「통화 내용 안내」 · 「동행상담 녹음 안내」 주소(/fp/<토큰>?rec=<녹음>) — 그 업체를 열고 녹음을 바로 띄운다 · 한 번 쓰면 주소에서 뺌(뒤로 가기 · 새로 고침에 다시 안 뜨게)
  useEffect(() => {
    if (!data) return;
    const rid = new URLSearchParams(window.location.search).get('rec');
    if (!rid) return;
    try { window.history.replaceState(window.history.state, '', window.location.pathname); } catch { /* 주소 그대로여도 됨 */ }
    // 통화 녹음 = 그 업체 「통화 녹음」 · 동행상담 녹음 = 그 상담의 「녹음 · 대화 글」(2026-10-28)
    fetch(`/api/rec/fp/${encodeURIComponent(fpId)}/recs/${encodeURIComponent(rid)}`).then(r => r.json())
      .then((j: { success?: boolean; data?: { kind: string; company_id: string | null; consultation_id: string | null } }) => {
        const w = j.success ? j.data : undefined;
        const cons = w && w.consultation_id ? data.consultations.find(c => c.id === w.consultation_id) : undefined;
        const co = w && w.company_id ? data.companies.find(c => c.id === w.company_id) : undefined;
        if (w && w.kind === 'call' && co) { setSelId(co.id); setCallFor(co); }
        else if (cons) { if (co) setSelId(co.id); setRecFor(cons); }
        else toast('이 녹음은 볼 수 없어요');
      }).catch(() => toast('잠시 연결이 안 돼요'));
  }, [data]);   // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!wide) window.scrollTo(0, selId ? 0 : listY.current); }, [selId, wide]);
  // 휴대폰 뒤로 가기 = 업체 자세히 닫기(앱 전체 덫이 막고 있던 것 · 2026-10-12 2차 검수)
  useEffect(() => {
    const on = () => setSelId(null);
    window.addEventListener('popstate', on);
    return () => window.removeEventListener('popstate', on);
  }, []);

  const contact = useContact(data ? data.contact : null, toast);
  const today = kstToday();
  // 주 색 = 팀장님 소속 보험사 색(버튼 · 진행 막대 · 지금 단계 · 고른 줄 띠 · 2026-10-13 지도사 「FP 포털도 한화생명 색상」) — 글자에 쓰여 대비 4.5로 어둡게
  setBrand(data ? data.insuranceColor : null);   // 보험사 색 — 맨 위 띠 · 카드 강조

  const sorted = useMemo(() => {
    if (!data) return [];
    return data.companies.map(c => ({ c, rep: representative(data.tasks.filter(t => t.company_id === c.id), 'fp', today) }))
      .sort((a, b) => (a.rep && b.rep ? compareUrgency(a.rep.urg, b.rep.urg) : a.rep ? -1 : b.rep ? 1 : a.c.name.localeCompare(b.c.name, 'ko')));
  }, [data, today]);

  const scheduled = useMemo(() => (data ? data.consultations.filter(c => c.status === 'scheduled' && d10(c.date) >= today).sort((a, b) => d10(a.date).localeCompare(d10(b.date))) : []), [data, today]);

  if (error) return <Shell><div style={{ padding: '120px 32px', textAlign: 'center' }}><Para>{error}</Para></div></Shell>;
  if (!data) return <Shell><div style={{ padding: '120px 32px', textAlign: 'center', color: G.g600, fontSize: 16 }}>불러오는 중이에요</div></Shell>;

  const { fp } = data;
  const heading = `${short(fp.name, 10)} 팀장님 × ${JIDO}`;   // 제목 — 늘 이 꼴(이름이 길면 이름만 줄임)
  const asof = asOfLabel(null);
  const pcSel = wide ? (selId || (sorted[0] ? sorted[0].c.id : null)) : selId;
  const q = search.trim();
  const shown = q ? sorted.filter(x => x.c.name.includes(q) || (x.c.ceo || '').includes(q) || (x.c.real_ceo || '').includes(q) || (x.c.biz_no || '').includes(q)) : sorted;

  /** 파일 열기 — 휴대폰(아이폰 · 카톡 안 브라우저)은 받아 온 주소로 같은 창을 넘기고, PC 는 누르는 순간 빈 창을 먼저 연다(뒤늦은 새 창은 팝업으로 막힘) */
  const openFile = async (key: string, api: string) => {
    if (opening) return;
    setOpening(key);
    const w = isTouch() ? null : window.open('', '_blank');
    try {
      const r = await fetch(api);
      const j: { success?: boolean; data?: { url?: string } } = await r.json();
      const url = j.success && j.data && j.data.url ? j.data.url : '';
      if (!url) throw new Error('no url');
      if (w) w.location.href = url; else window.location.href = url;
    } catch { if (w) w.close(); toast('파일을 열 수 없어요. 잠시 뒤 다시 눌러 주세요'); }
    finally { setOpening(null); }
  };
  const download = (f: FileRef) => { if (f.s3_key) void openFile(f.s3_key, '/api/tm/portal-download?key=' + encodeURIComponent(f.s3_key) + '&fileName=' + encodeURIComponent(f.name)); };
  const downloadStorage = (id: string) => { void openFile(id, `/api/tm/fp-storage/${encodeURIComponent(fpId)}/download/${encodeURIComponent(id)}`); };
  /** 팀장님이 「답하기」를 눌렀다 — 서버에 남기고 이 화면도 바로 「답 확인 중」 */
  const claim = (stepId: string, via: 'kakao' | 'sms') => {
    if (!PREVIEW) void fetch(`/api/tm/fp-portal-data/${encodeURIComponent(fpId)}/replied`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ stepId, via }) }).catch(() => undefined);
    setData(d => (d ? { ...d, tasks: d.tasks.map(t => ({ ...t, steps: t.steps.map(x => (x.id === stepId && x.status === '회신대기' && !x.claimed_by ? { ...x, claimed_by: 'FP', claimed_at: kstToday() } : x)) })) } : d));
  };
  const openCompany = (id: string) => { if (!wide) { listY.current = window.scrollY; try { window.history.pushState({ fp: id }, ''); } catch { /* 기록 실패해도 화면은 열림 */ } } setSelId(id); };
  const closeCompany = () => { if (window.history.state && (window.history.state as { fp?: string }).fp) window.history.back(); else setSelId(null); };

  /* ── 왼쪽(모바일 첫 화면) ── */
  // 제목 앞 동그란 점은 뺐다 — 점부터 찍는 꼴이 만든 화면처럼 보임(2026-10-18 지도사 「앞에 점 없애야」) · 보험사 색은 맨 위 띠(BrandStrip)로만
  // 녹음 · 대화 글(2026-10-28) — 상담 기록의 「보기」 · PC 가운데 창 · 휴대폰 아래 시트(공용 Sheet)
  const recSheet = recFor ? (
    <Sheet label="녹음 · 대화 글" onClose={() => setRecFor(null)} maxWidth={760} z={60}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
        <span style={{ flex: 1, fontSize: 20, fontWeight: 700, color: G.g900 }}>녹음 · 대화 글</span>
        <span style={{ fontSize: 15, color: G.g600 }}>{mdLong(recFor.date)}</span>
      </div>
      <PortalRecordings url={`/api/rec/fp/${encodeURIComponent(fpId)}/consultations/${recFor.id}`} />
      <div style={{ marginTop: 12 }}><Button kind="gray" h={48} onClick={() => setRecFor(null)}>닫기</Button></div>
    </Sheet>
  ) : null;
  const callSheet = callFor ? (
    <Sheet label="통화 녹음 · 대화 글" onClose={() => setCallFor(null)} maxWidth={760} z={60}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, marginBottom: 12 }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 20, fontWeight: 700, color: G.g900, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>통화 녹음 · 대화 글</span>
        <span style={{ fontSize: 15, color: G.g600 }}>{callFor.name}</span>
      </div>
      <PortalRecordings url={`/api/rec/fp/${encodeURIComponent(fpId)}/companies/${callFor.id}`} />
      <div style={{ marginTop: 12 }}><Button kind="gray" h={48} onClick={() => setCallFor(null)}>닫기</Button></div>
    </Sheet>
  ) : null;
  const consultGroup = scheduled.length > 0 && (
    <>
      <ListHeader title="동행 상담" right={`${scheduled.length}건`} />
      {scheduled.map(c => {
        const comp = data.companies.find(x => x.id === c.company_id);
        return (
          <ListRow key={c.id} left={<Asset icon="cal" />} title={`${c.company_name || (comp ? comp.name : '업체')}${c.location ? ' · ' + c.location : ''}`}
            sub={`${md(c.date)}${c.time ? ' ' + c.time : ''}`} right={<Badge spec={leftBadge(daysBetween(today, d10(c.date)), '뒤')} />}
            arrow={!!comp} onClick={comp ? () => openCompany(comp.id) : undefined} clip />
        );
      })}
    </>
  );
  const clientGroup = (
    <>
      <ListHeader title="담당 고객" right={`${data.companies.length}곳`} />
      <label className="fp-search" style={{ margin: '4px 20px 0', height: 44, borderRadius: 12, background: '#fff', boxShadow: `inset 0 0 0 1px ${G.g500}`, display: 'flex', alignItems: 'center', gap: 8, padding: '0 14px' }}>
        <Icon name="search" size={18} color={G.g600} />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="업체 이름으로 찾기" aria-label="업체 이름으로 찾기"
          style={{ flex: 1, minWidth: 0, border: 'none', outline: 'none', fontSize: 16, color: G.g900, background: 'transparent', fontFamily: 'inherit' }} />
      </label>
      <div style={{ padding: '8px 20px 4px', fontSize: 15, color: G.g700 }}>업체마다 가장 급한 일 하나씩, 급한 순서로 보여요</div>
      {shown.length === 0 && <div style={{ padding: '20px', fontSize: 15, color: G.g600 }}>{q ? '찾는 업체가 없어요' : '담당 고객이 없어요'}</div>}
      {shown.map(({ c, rep }) => {
        const nAct = data.tasks.filter(t => t.company_id === c.id && !isEnded(t)).length;
        const sub: ReactNode = rep
          ? <span style={{ display: 'flex', minWidth: 0 }}><span style={clamp(1)}>{rep.task.type}</span>{nAct > 1 && <span style={{ flexShrink: 0, whiteSpace: 'nowrap' }}>&nbsp;외 {nAct - 1}개</span>}</span>
          : '진행 중인 일 없음';
        return (
          <ListRow key={c.id} left={<Asset ch={initialOf(c.name)} />} title={c.name} sub={sub} clip
            badge={rep && taskBadge(rep.task, 'fp', today) ? <Badge spec={taskBadge(rep.task, 'fp', today)} /> : undefined} arrow onClick={() => openCompany(c.id)}
            selected={wide && pcSel === c.id} />
        );
      })}
      <div style={{ height: 4 }} />
    </>
  );

  /* ── 오른쪽(모바일 자세히) ── */
  const detail = (cid: string, pc: boolean) => {
    const comp = data.companies.find(c => c.id === cid);
    if (!comp) return null;
    const ts = data.tasks.filter(t => t.company_id === cid);
    const act = ts.filter(t => !isEnded(t));
    const ended = ts.filter(isEnded);
    const memos = (data.comments || []).filter(c => c.company_id === cid);
    const ofCo = (c: PConsult) => c.company_id === cid || (c.company_ids || []).includes(cid);   // 관계사로 함께 상담한 회사도(2026-10-18)
    const history = data.consultations.filter(c => ofCo(c) && c.status !== 'scheduled');
    const shared = files.filter(f => f.company_id === cid);
    const m = pc ? '0 0 12px' : '0 12px 12px';
    const fpT = fp.title || '팀장';   // FP 화면에서 FP 호칭 = 팀장님(지도사 2026-10-12)

    const taskBlock = (t: PTask, i: number) => {
      const p = progressOf(t); const nl = nowLine(t, 'fp', JIDO, fpT); const multi = p.total > 1;
      const bd = taskBadge(t, 'fp', today);
      const isOpen = open[t.id] !== undefined ? open[t.id] : (pc || nl.wait);
      return (
        <div key={t.id}>
          {i > 0 && !pc && <Divider />}
          <ListRow left={<Asset icon={taskIcon(t.type)} />} title={t.type} sub={stageText(t)} subRight={bd ? <Badge spec={bd} /> : undefined} />
          {/* 지금 · 최근 소식은 고객 진행 안내와 같은 짜임(TaskSummary) · 펼치기 줄은 카드 맨 아래(FoldFooter · 2026-10-27 지도사 「조잡하다」) */}
          <TaskSummary pct={p.pct} bar={multi} now={nowSentence(nl)} wait={nl.wait} news={taskNews(t, md)}>
            {t.terms && <TermsBox terms={t.terms} label={String(t.type || '').replace(/^정책자금 대출\s*·\s*/, '').replace(/^정책자금 대출$/, '')} />}
            {t.status === '중단' && t.round_result && <RoundResultBox r={t.round_result} label={String(t.type || '').replace(/^정책자금 대출\s*·\s*/, '').replace(/^정책자금 대출$/, '')} />}
          </TaskSummary>
          {(isOpen || !multi) && t.steps.map((s, si) => {
            const st = stepState(t, s);   // 결과 미○○ · 다시 신청 단계 포함(2026-10-18)
            const can = waitsOn(s, 'fp') && (contact.canKakao || contact.canSms);
            const date: ReactNode = can ? <TextLink color={TEAL} icon={reply === s.id ? 'u' : 'r'} onClick={() => setReply(r => (r === s.id ? null : s.id))}>답하기</TextLink> : stepDate(s, 'fp');
            return (
              <div key={s.id}>
                <StepRow n={si + 1} name={s.name} note={stepNote(s, 'fp', JIDO, fpT)} date={date} state={st} bold={!!s.fixed} />
                {reply === s.id && (
                  <div style={{ background: '#F2FBFA', padding: '0 20px 14px 74px' }}>
                    <AskButtons c={contact} h={44} context={`${comp.name}, ${t.type}, ${s.name}`} smsBody={`[${comp.name}] ${t.type} ${s.name} 건, FP ${fp.name} 답드려요: `} onUse={via => claim(s.id, via)} />
                  </div>
                )}
              </div>
            );
          })}
          {multi && <FoldFooter open={isOpen} count={p.total} onClick={() => setOpen(o => ({ ...o, [t.id]: !isOpen }))} />}
          <div style={{ height: 8 }} />
        </div>
      );
    };

    const head = <Top title={short(comp.name, 30)} subTop={comp.ceo || comp.real_ceo ? `${ceoShown(comp)} 대표` : ''} padT={pc ? 22 : 8} padB={pc ? 22 : 16} side={pc ? 20 : 32} />;
    const upcoming = scheduled.filter(ofCo);
    const upGroup = upcoming.length > 0 && (
      <Group m={m}>
        <ListHeader title="동행 상담" right={`${upcoming.length}건`} />
        {upcoming.map(c => <ListRow key={c.id} left={<Asset icon="cal" />} title={`${mdLong(c.date)}${c.time ? ' ' + c.time : ''}`} sub={c.location || ''} right={<Badge spec={leftBadge(daysBetween(today, d10(c.date)), '뒤')} />} />)}
      </Group>
    );
    return (
      <>
        {pc ? <section style={{ background: '#fff', borderRadius: 20, overflow: 'hidden', marginBottom: 12 }}>{head}</section> : <>{head}<div style={{ height: 12 }} /></>}
        {/* 대표님께 전달할 것(2026-10-28) — 리포트 · 필요서류 제출 · 대표님 진행 안내만 · 이 화면은 팀장님 전용 */}
        <ForwardToCeo key={'fw-' + cid} fpId={fpId} companyId={cid} companyName={comp.name} m={m} toast={toast} place="top" />
        {upGroup}
        {pc
          ? (
            <>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 20px 12px' }}>
                <h2 style={{ margin: 0, fontSize: 20, fontWeight: 700 }}>진행 중인 일</h2><span style={{ fontSize: 15, color: G.g600 }}>{act.length}개</span>
              </div>
              {act.length === 0 && <Group m={m} pad="24px 20px"><Para color={G.g600}>진행 중인 일이 없어요</Para></Group>}
              {act.map((t, i) => <Group key={t.id} m={m} pad="8px 0 0">{taskBlock(t, i)}</Group>)}
            </>
          )
          : <Group m={m}><ListHeader title="진행 중인 일" right={`${act.length}개`} />{act.length ? act.map(taskBlock) : <div style={{ padding: '4px 20px 20px' }}><Para color={G.g700}>진행 중인 일이 없어요</Para></div>}</Group>}
        {/* 통화 녹음 — 진행 중인 일 바로 밑(2026-10-29 지도사 「통화 녹음한 거 여기서도 보여야 해 · 진행 중인 일 밑에」 · 예전엔 상담 기록 아래 맨 끝) */}
        {(comp.call_rec_count || 0) > 0 && (
          <Group m={m}>
            <ListHeader title="통화 녹음" right={`${comp.call_rec_count}건`} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '4px 20px 14px' }}>
              <Icon name="mic" size={18} color={G.g600} />
              <span style={{ flex: 1, minWidth: 0, fontSize: 15, color: G.g800 }}>대표님과 통화한 녹음 · 대화 글</span>
              <Button kind="weak" h={44} w={72} full={false} onClick={() => setCallFor(comp)}>보기</Button>
            </div>
          </Group>
        )}
        {/* 다 보낸 뒤엔 「대표님께 보낸 것」 한 줄로 여기(2026-10-28) */}
        <ForwardToCeo key={'fwb-' + cid} fpId={fpId} companyId={cid} companyName={comp.name} m={m} toast={toast} place="bottom" />
        {memos.length > 0 && (
          <Group m={m}>
            <ListHeader title={`${JIDO}의 안내`} />
            {memos.map((cm, i) => (
              <div key={cm.id}>{i > 0 && <Divider full />}
                <div style={{ padding: '8px 20px 12px' }}>
                  <div style={{ fontSize: 14, color: G.g600, marginBottom: 4 }}>{mdLong(cm.created_at)}</div>
                  <MoreText lines={5}>{cm.content}</MoreText>
                </div>
              </div>
            ))}
          </Group>
        )}
        {history.length > 0 && (
          <Group m={m}>
            <ListHeader title="상담 기록" right={`${history.length}건`} />
            {history.map((c, i) => (
              <div key={c.id}>{i > 0 && <Divider full />}
                <div style={{ padding: '8px 20px 12px' }}>
                  <div style={{ fontSize: 14, color: G.g600, marginBottom: 4 }}>{mdLong(c.date)}{c.time ? ' ' + c.time : ''}{c.location ? ' · ' + c.location : ''}</div>
                  {(c.content || c.notes) && <MoreText lines={5}>{c.content || c.notes}</MoreText>}
                  {(c.files || []).map((f, fi) => (
                    <div key={fi} style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                      <Icon name="doc" size={18} color={G.g600} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: 15, color: G.g800, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{f.name}</span>
                      <Button kind="weak" h={44} w={72} full={false} onClick={() => download(f)}>{opening === f.s3_key ? '여는 중' : '받기'}</Button>
                    </div>
                  ))}
                  {(c.rec_count || 0) > 0 && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8 }}>
                      <Icon name="mic" size={18} color={G.g600} />
                      <span style={{ flex: 1, minWidth: 0, fontSize: 15, color: G.g800 }}>녹음 · 대화 글 {c.rec_count}개</span>
                      <Button kind="weak" h={44} w={72} full={false} onClick={() => setRecFor(c)}>보기</Button>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </Group>
        )}
        {shared.length > 0 && (
          <Group m={m}>
            <ListHeader title="공유 자료" right={`${shared.length}건`} />
            {shared.map(f => (
              <ListRow key={f.id} left={<Asset icon="folder" />} title={f.item_title || f.file_name} clip
                sub={[f.uploaded_at ? md(f.uploaded_at) + ' 저장' : '', f.source === '자료수집' ? '대표님이 올린 자료' : (f.source || '')].filter(Boolean).join(' · ')}
                right={<Button kind="weak" h={44} w={72} full={false} onClick={() => downloadStorage(f.id)}>{opening === f.id ? '여는 중' : '받기'}</Button>} />
            ))}
          </Group>
        )}
        {ended.length > 0 && (
          <Group m={m}>
            <ListHeader title="끝났거나 멈춘 일" right={`${ended.length}개`} />
            {ended.map(t => (
              <ListRow key={t.id} left={<Asset icon={taskIcon(t.type)} />} title={t.type} dim right={<Badge spec={taskBadge(t, 'fp', today)} />}
                sub={t.status === '중단' ? (t.round_closed ? roundClosedText(t) : `${md(t.stopped_at)} 멈췄어요${t.stop_reason ? ` (${t.stop_reason})` : ''}`) : '끝났어요'} />
            ))}
          </Group>
        )}
        {/* 신청할 수 있는 지원사업 — 맨 아래(2026-10-28 지도사 「가장 밑에 배치해」 · 업체 일 먼저) */}
        <GovForCompany key={'gov-' + cid} fpId={fpId} companyId={cid} companyName={comp.name} fpName={fp.name} contact={contact} m={m} />
      </>
    );
  };

  /* ── PC ── */
  if (wide) {
    return (
      <Shell wide>
        <div style={{ height: 64, background: '#fff', borderBottom: `1px solid ${G.g200}` }}>
          <div style={{ maxWidth: 1120, margin: '0 auto', height: '100%', display: 'flex', alignItems: 'center', gap: 12, padding: '0 20px' }}>
            <span style={{ fontSize: 17, fontWeight: 700, flexShrink: 0, whiteSpace: 'nowrap' }}>{heading}</span>
            <span style={{ fontSize: 15, color: G.g600, flexShrink: 1, ...clamp(1) }}>{fp.company}</span>
            <span style={{ flex: 1 }} />
            <span style={{ fontSize: 15, color: G.g600, flexShrink: 0 }}>{asof}</span>
          </div>
        </div>
        <div style={{ maxWidth: 1168, margin: '0 auto', padding: '28px 24px', display: 'grid', gridTemplateColumns: '400px 1fr', gap: 20, alignItems: 'start' }}>
          <div style={{ minWidth: 0 }}>
            {consultGroup && <Group m="0 0 12px">{consultGroup}</Group>}
            <Group m="0 0 12px">{clientGroup}</Group>
          </div>
          <div style={{ minWidth: 0 }}>{pcSel ? detail(pcSel, true) : <Group m="0 0 12px" pad="28px 20px"><Para color={G.g600}>담당 고객이 없어요</Para></Group>}</div>
        </div>
        {contact.sheet}{recSheet}{callSheet}{toastNode}
      </Shell>
    );
  }

  /* ── 모바일 ── */
  if (selId) {
    return (
      <Shell>
        <AppBar onBack={closeCompany} />
        {detail(selId, false)}
        <div style={{ height: 24 }} />
        {contact.sheet}{recSheet}{callSheet}{toastNode}
      </Shell>
    );
  }
  return (
    <Shell>
      <Top title={heading} subTop={<span style={{ display: 'block', ...clamp(1) }}>{fp.company}</span>} padT={28} extra={<AsOf text={asof} mt={8} />} />
      <div style={{ height: 12 }} />
      {consultGroup && <Group>{consultGroup}</Group>}
      <Group>{clientGroup}</Group>
      <div style={{ height: 24 }} />
      {recSheet}{callSheet}{toastNode}
    </Shell>
  );
}

function Shell({ children, wide }: { children: ReactNode; wide?: boolean }) {
  return <div className="tds-root" style={{ overflowX: 'hidden' }}><PreviewBar who="팀장님" /><BrandStrip /><div style={wide ? undefined : { maxWidth: 640, margin: '0 auto' }}>{children}</div></div>;
}
